/**
 * Histórico local dos erros que o sistema mostrou na tela, do dia corrente.
 *
 * Existe por um caso concreto: o aviso de erro é um snackbar. O operador fecha sem ler, ou ele
 * desaparece, e aí ele liga para o Suporte sem ter o código do erro para informar. O código é
 * justamente o dado que faz o Suporte achar a ocorrência exata no banco com um SELECT.
 *
 * Fica em localStorage de propósito, e não numa tabela: não há controle por usuário para manter, não
 * há endpoint novo, não há permissão nova. A ocorrência de verdade já está no banco, gravada pelo log
 * de erro interno; aqui é só o recibo do que ESTE navegador mostrou.
 *
 * Este arquivo não importa nada pelo alias `@/` de propósito, para poder ser testado direto pelo
 * jest (que neste projeto não tem moduleNameMapper para o alias). Import relativo pode.
 */

import { semSufixoDeCodigo } from '../helpers/axios-helpers'

export const HISTORICO_DE_ERROS_KEY = 'HISTORICO_DE_ERROS'

/** Teto de entradas. Só serve ao dia corrente, então 30 cobre até um dia muito ruim. */
export const MAXIMO_DE_ERROS = 30

/**
 * Janela em que um erro repetido não vira entrada nova, e sim `vezes + 1`.
 *
 * Sem isso, uma tela que repete a chamada em loop enche as 30 vagas em segundos e empurra para fora
 * o erro que o usuário está tentando recuperar.
 */
const JANELA_DE_REPETICAO_MS = 10_000

const LIMITE_TEXTO = 500
const LIMITE_MENSAGEM = 2000
const LIMITE_STACK = 4000

export type ErroNoHistorico = {
	id: string
	/** ISO 8601. Guardado cru para poder formatar no fuso do navegador na hora de exibir. */
	ocorridoEm: string
	/** Código curto do erro interno (ex.: ABC3-K7Q2-M9). Vazio em erro que não é interno. */
	codigo: string
	/** O que apareceu no snackbar. */
	texto: string
	/** Mensagem técnica do backend, quando veio (só chega para chamador autenticado). */
	mensagem: string
	stackTrace: string
	/** Em que tela o usuário estava. Sai de window.location, sem acoplar ao router. */
	rota: string
	/** Quantas vezes o mesmo erro apareceu dentro da janela de repetição. */
	vezes: number
}

export type ErroParaRegistrar = {
	codigo?: string
	texto?: string
	mensagem?: string
	stackTrace?: string
	rota?: string
	ocorridoEm?: string
}

/** Dia no calendário LOCAL (não UTC): "do dia" é do ponto de vista de quem está na loja. */
const diaLocal = (data: Date): string => {
	const mes = `${data.getMonth() + 1}`.padStart(2, '0')
	const dia = `${data.getDate()}`.padStart(2, '0')
	return `${data.getFullYear()}-${mes}-${dia}`
}

const ehDeHoje = (iso: string): boolean => {
	const data = new Date(iso)
	if (isNaN(data.getTime())) return false
	return diaLocal(data) === diaLocal(new Date())
}

const truncar = (valor: unknown, limite: number): string => {
	if (typeof valor !== 'string' || !valor) return ''
	return valor.length <= limite ? valor : `${valor.slice(0, limite)}...`
}

/**
 * Chave de comparação para a janela de repetição. A mensagem técnica é preferida ao texto porque o
 * texto do erro interno carrega o código, e o código muda a cada ocorrência: comparar por ele faria
 * a deduplicação nunca casar justamente no caso que mais repete.
 */
const chaveDeRepeticao = (erro: { mensagem: string; texto: string }): string =>
	erro.mensagem || erro.texto

const ler = (): ErroNoHistorico[] => {
	try {
		const bruto = JSON.parse(localStorage.getItem(HISTORICO_DE_ERROS_KEY) || '[]')
		if (!Array.isArray(bruto)) return []
		return bruto.filter(item => item && typeof item === 'object' && ehDeHoje(item.ocorridoEm))
	} catch {
		return []
	}
}

/**
 * Grava tolerando cota estourada. O localStorage é compartilhado com etiquetas, filtros e produtos
 * importados, então QuotaExceededError é possível. Nesse caso vai descartando informação, do menos
 * para o mais importante, e no pior caso desiste em silêncio: um histórico que não caiba não pode
 * derrubar o tratamento de erro do sistema.
 */
const gravar = (historico: ErroNoHistorico[]): ErroNoHistorico[] => {
	const tentativas: ErroNoHistorico[][] = [
		historico,
		historico.map(erro => ({ ...erro, stackTrace: '' })),
		historico.slice(0, 5).map(erro => ({ ...erro, stackTrace: '', mensagem: truncar(erro.mensagem, 200) })),
	]

	for (const tentativa of tentativas) {
		try {
			localStorage.setItem(HISTORICO_DE_ERROS_KEY, JSON.stringify(tentativa))
			return tentativa
		} catch {
			continue
		}
	}
	return historico
}

/** Erros do dia corrente, do mais recente para o mais antigo. */
export const lerHistoricoDeErros = (): ErroNoHistorico[] => ler()

/**
 * Registra o erro e devolve o histórico já atualizado.
 *
 * NUNCA lança: é chamado de dentro do caminho de tratamento de erro, e uma exceção aqui trocaria a
 * mensagem que o usuário precisa ver por outra, sem relação nenhuma com o problema real.
 */
export const registrarErroNoHistorico = (novo: ErroParaRegistrar): ErroNoHistorico[] => {
	try {
		const agora = novo.ocorridoEm || new Date().toISOString()
		const entrada: ErroNoHistorico = {
			id: `${new Date(agora).getTime()}-${Math.random().toString(36).slice(2, 8)}`,
			ocorridoEm: agora,
			codigo: truncar(novo.codigo, 32),
			texto: truncar(novo.texto, LIMITE_TEXTO),
			mensagem: truncar(novo.mensagem, LIMITE_MENSAGEM),
			stackTrace: truncar(novo.stackTrace, LIMITE_STACK),
			rota: truncar(novo.rota, 300),
			vezes: 1,
		}

		if (!entrada.texto && !entrada.mensagem && !entrada.codigo) return ler()

		const historico = ler()
		const primeiro = historico[0]
		const repetido = primeiro
			&& chaveDeRepeticao(primeiro) === chaveDeRepeticao(entrada)
			&& new Date(agora).getTime() - new Date(primeiro.ocorridoEm).getTime() < JANELA_DE_REPETICAO_MS

		if (repetido) {
			// Mantém a entrada, avança o horário e fica com o código MAIS RECENTE: qualquer um dos
			// códigos acha a ocorrência, e o último é o que o usuário acabou de ver na tela.
			historico[0] = {
				...primeiro,
				ocorridoEm: agora,
				codigo: entrada.codigo || primeiro.codigo,
				stackTrace: entrada.stackTrace || primeiro.stackTrace,
				vezes: primeiro.vezes + 1,
			}
			return gravar(historico)
		}

		return gravar([entrada, ...historico].slice(0, MAXIMO_DE_ERROS))
	} catch {
		return []
	}
}

export const limparHistoricoDeErros = (): void => {
	try {
		localStorage.removeItem(HISTORICO_DE_ERROS_KEY)
	} catch { /* histórico não é crítico */ }
}

/**
 * Texto do aviso sem o "Código do erro: XXXX-XXXX-XX" do fim.
 *
 * Serve só para a linha resumida da lista, onde o código já aparece no seu próprio destaque e
 * repetir só atrapalha a leitura. O texto guardado continua sendo o que apareceu na tela, sem edição.
 */
export const resumoDoErro = (erro: ErroNoHistorico): string => {
	if (!erro.codigo) return erro.texto
	return semSufixoDeCodigo(erro.texto) || erro.texto
}

